import { useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { Card, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { StepFooterNav } from "../../shared/NavigationFooter";
import { useShovellerSignupContext } from "../../../../context/shovllerSignupFormContext";

const SERVICE_OPTIONS = [
  {
    id: "shovel",
    label: "Snow Shoveling",
    desc: "Shovel or Pusher",
    imageUrl:
      "https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/snow-shovel-2.svg",
  },
  {
    id: "electricSunblower",
    label: "Snow Shoveling",
    desc: "Electric Sunblower",
    imageUrl:
      "https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/shoveling-machine.svg",
  },
  {
    id: "gasSunblower",
    label: "Snow Shoveling",
    desc: "Gas Sunblower",
    imageUrl:
      "https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/shoveling-machine.svg",
  },
  {
    id: "reelPushMower",
    label: "Lawn mowing",
    desc: "Manual push reel mower",
    imageUrl:
      "https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/man-mowing.svg",
  },
  {
    id: "electricMower",
    label: "Lawn mowing",
    desc: "Electric mower",
    imageUrl:
      "https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/mowing.svg",
  },
  {
    id: "gasMower",
    label: "Lawn mowing",
    desc: "Gas mower",
    imageUrl: "https://i.imgur.com/hjL9ILS.png",
  },
  {
    id: "electricTrimmer",
    label: "Edge trimming",
    desc: "(non-electric / rotary)",
    imageUrl: "https://i.imgur.com/X2Npa44.png",
  },
  {
    id: "electricStringTrimmer",
    label: "Edge trimming",
    desc: "Electric string trimmer",
    imageUrl:
      "https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/broom.svg",
  },
  {
    id: "gasTrimmer",
    label: "Edge trimming",
    desc: "Gas string trimmer",
    imageUrl:
      "https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/broom.svg",
  },
  {
    id: "leafRaking",
    label: "Leaf Raking",
    desc: "",
    imageUrl: "https://i.imgur.com/3OSMtre.png",
  },
];

export function Step2({ nextStep, preStep, currentStep, totalSteps }) {
  const { setFormData, selectedServices, setSelectedServices } =
    useShovellerSignupContext();
  const navigate = useNavigate();

  const handleNext = () => {
    setFormData((prevData) => ({
      ...prevData,
      servicesProvide: selectedServices,
    }));
    nextStep();
  };

  const handleBack = () => {
    preStep();
  };

  const handleServiceClick = (service) => {
    setSelectedServices((prevSelected) =>
      prevSelected.includes(service)
        ? prevSelected.filter((item) => item !== service)
        : [...prevSelected, service]
    );
  };

  const isSelected = (service) => selectedServices.includes(service);

  return (
    <div className="min-h-screen py-6 lg:py-12">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <h1 className="text-2xl sm:text-4xl font-bold">Services You Provide</h1>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Choose what services you want to provide (check all that apply, you
            may change this later).
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:justify-center gap-4">
          {SERVICE_OPTIONS.map((option, idx) => {
            const active = isSelected(option.id);

            return (
              <motion.div
                key={option.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleServiceClick(option.id)}
                className={`cursor-pointer rounded-lg transition w-full sm:w-[31%] max-w-sm ${active
                    ? "ring-2 ring-primary bg-primary/10 shadow-md"
                    : "ring-2 ring-gray-100 hover:shadow-sm"
                  }`}
              >
                <Card className="h-full p-4 flex flex-col items-center justify-center text-center bg-transparent border-none relative">
                  {/* Service Icon */}
                  <img
                    loading="lazy"
                    src={option.imageUrl}
                    alt={option.label}
                    className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                  />

                  <CardContent className="p-2 font-medium">
                    {option.label}
                    {option.desc && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {option.desc}
                      </p>
                    )}
                  </CardContent>

                  {/* Checkmark if selected */}
                  {active && (
                    <img
                      loading="lazy"
                      src="https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/check-mark.svg"
                      className="absolute z-10 w-6 h-6 left-2 top-2"
                      alt="Selected"
                    />
                  )}
                </Card>
              </motion.div>
            );
          })}
        </div>


        {/* Note */}
        <p className="text-xs text-muted-foreground text-center max-w-2xl mx-auto">
          All equipment will be provided by the homeowner, but you must know how
          to operate the tools/machines on your own (specific start and stop
          instructions will be provided by homeowner).
        </p>

        {/* Footer Navigation */}
        <StepFooterNav
          currentStep={currentStep}
          totalSteps={totalSteps}
          onBack={handleBack || (() => navigate(-1))}
          onNext={handleNext}
          nextDisabled={selectedServices.length === 0}
        />
      </div>
    </div>
  );
}
